import React from "react";
import {
  DocumentTextIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  BucketObject,
  FileBrowserBadge,
  FileBrowserPanel,
  keyOf,
} from "@via/pages/data/data-sources/utils/helpers.tsx";
import { classNames, truncateMiddle } from "@via/utils/helpers.tsx";
import { Spinner } from "@via/components/loading/loading";

type CartVariant = "default" | "dataPage";

interface CartProps {
  items: BucketObject[];
  onRemove: (key: string) => void;
  onClear: () => void;
  onContinue?: () => void;
  submitButtonText?: string;
  submitButtonLoading?: boolean;
  /** Layout variant: 'default' for modals/downloads, 'dataPage' for data page with flex layout */
  variant?: CartVariant;
}

const Cart: React.FC<CartProps> = ({
  items,
  onRemove,
  onClear,
  onContinue,
  submitButtonText = "Continue with",
  submitButtonLoading = false,
  variant = "default",
}) => {
  const isDataPage = variant === "dataPage";

  return (
    <FileBrowserPanel
      className={classNames(
        "flex h-full flex-col",
        isDataPage ? "overflow-hidden" : ""
      )}
      dataTestId="cartWrapper">
      <div
        className={classNames(
          "flex items-center justify-between px-4 py-4",
          isDataPage ? "shrink-0" : ""
        )}>
        {isDataPage ? (
          <div className="flex flex-col">
            <div
              className="flex items-center text-lg font-semibold text-gray-900"
              data-testid="cartFilesCounter">
              Dataset <FileBrowserBadge count={items.length} />
            </div>

            <div className="text-sm text-gray-400">
              Files currently selected for this dataset.
            </div>
          </div>
        ) : (
          <div
            className="flex items-center text-lg font-semibold text-gray-900"
            data-testid="cartFilesCounter">
            Cart <FileBrowserBadge count={items.length} />
          </div>
        )}

        {items.length > 0 && (
          <button
            data-testid="clearFilesCartButton"
            onClick={onClear}
            className="inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
            <TrashIcon className="h-4 w-4" /> Clear
          </button>
        )}
      </div>

      <div
        className={classNames(
          "flex-1 divide-y overflow-auto",
          isDataPage ? "min-h-0" : "max-h-[70vh]"
        )}>
        {items.length === 0 ? (
          <div className="px-4 py-8 text-sm text-gray-500">
            {isDataPage ? "No files added yet." : "No files in cart."}
          </div>
        ) : (
          items.map(it => {
            const displayName = it.displayName || it.name;
            return (
              <div
                key={keyOf(it)}
                className="flex items-center gap-3 px-4 py-3">
                <DocumentTextIcon className="h-5 w-5 text-gray-700" />
                <div className="min-w-0 flex-1" data-testid={displayName}>
                  <div
                    className="truncate text-sm font-medium text-gray-900"
                    title={displayName}>
                    {truncateMiddle(displayName, 50)}
                  </div>
                  {it.path ? (
                    <div
                      className="truncate text-xs text-gray-500"
                      title={it.path}>
                      {it.path}
                    </div>
                  ) : null}
                </div>
                <button
                  onClick={() => onRemove(keyOf(it))}
                  className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                  aria-label={`Remove ${displayName} from cart`}>
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            );
          })
        )}
      </div>

      <div className={classNames("border-t p-4", isDataPage ? "shrink-0" : "")}>
        <button
          data-testid="continueWithFilesCartButton"
          onClick={onContinue}
          disabled={items.length === 0 || submitButtonLoading}
          className="w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-50">
          <Spinner loading={submitButtonLoading}>
            {submitButtonText} {items.length} File
            {items.length === 1 ? "" : "s"}
          </Spinner>
        </button>
      </div>
    </FileBrowserPanel>
  );
};

export default Cart;
